'use client';

import { useEventBus } from '@hc-frontend/core-ui';
import type {
  SendMetricEvent,
  SendMetricEventTarget,
} from '@hc-frontend/shells-shared';
import Script from 'next/script';
import type React from 'react';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

import type {
  SendConnectMeParams,
  TypConnectMeHolderProps,
} from './typ-connect-me-holder.types';
import TypConnectMeHolderContent from './typ-connect-me-holder-content';

interface ConnectMe {
  (key: string, value: string | Record<string, string | number>): void;
  q: IArguments[];
}

declare global {
  interface Window {
    g3cm?: ConnectMe;
    oninitconnectme?: () => void;
    oncallconnectme?: () => void;
    oncompletedconnectme?: () => void;
    onconnectedconnectme?: () => void;
    onfailedconnectme?: () => void;
  }
}

/**
 * See: https://docs.google.com/document/d/1qZhPJJuOTkR1k-laTGMXLQIBeksVgWNewJkENrnVNa8/edit
 */
export function TypConnectMeHolder({
  phoneNumber,
  destinationPhoneNumber,
  placement,
  sessionId,
  setCardHeaderText,
  metricName = 'connect-me',
  trustedFormCertUrl,
}: TypConnectMeHolderProps) {
  const eventBus = useEventBus();
  const ref = useRef<HTMLDivElement>(null);
  const [heroContent, setHeroContent] = useState<ReactElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      const source = ref.current;

      const sendConnectMe = ({ action }: SendConnectMeParams) =>
        eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
          id: 'connect-me-holder',
          topic: 'metric.client.message',
          source,
          metric: {
            name: metricName,
            tags: {
              action,
              placement,
            },
          },
        });

      const sendConnectMeCompleted = ({ action }: SendConnectMeParams) => {
        if (setCardHeaderText) {
          setCardHeaderText('Thank you!');
        }
        setHeroContent(<TypConnectMeHolderContent />);
        sendConnectMe({ action });
      };

      window.oninitconnectme = () => sendConnectMe({ action: 'Initialized' });
      window.oncallconnectme = () => sendConnectMe({ action: 'Call' });
      window.oncompletedconnectme = () =>
        sendConnectMeCompleted({ action: 'Completed' });
      window.onconnectedconnectme = () =>
        sendConnectMe({ action: 'Connected' });
      window.onfailedconnectme = () => sendConnectMe({ action: 'Failed' });

      if (!window.g3cm) {
        // @ts-expect-error This script if from the Connect-me documentation.
        // eslint-disable-next-line prefer-rest-params, @typescript-eslint/no-non-null-assertion
        window.g3cm = function () { (window.g3cm!.q = window.g3cm?.q || []).push(arguments) }; // prettier-ignore
      }
      window.g3cm?.('loadjquery', 'true');
      window.g3cm?.('phonenumber', phoneNumber);

      if (trustedFormCertUrl) {
        window.g3cm?.('trustedformid', trustedFormCertUrl);
      }

      window.g3cm?.('send', 'init');
    }
  }, [
    phoneNumber,
    placement,
    eventBus,
    setCardHeaderText,
    metricName,
    trustedFormCertUrl,
  ]);

  return (
    <>
      <Script
        async
        type="text/javascript"
        src="https://api.connectstreams.com/js/connectme-v3.min.js"
      />

      {heroContent}

      <div
        className="g3cm_holder"
        ref={ref}
        data-module="1247"
        data-calloninit="true"
        data-showoninit="true"
        data-oninit="oninitconnectme"
        data-oncall="oncallconnectme"
        data-oncompleted="oncompletedconnectme"
        data-onconnected="onconnectedconnectme"
        data-onfailed="onfailedconnectme"
        data-destination={destinationPhoneNumber}
        data-conversionid={sessionId}
        style={{ display: 'none' }}
      >
        Thank you for your inquiry. A representative will be in contact with you
        shortly.
      </div>
    </>
  );
}
